import KpiChart from "@app/components/kpi-chart";
import { useState } from "react";

// Relay types nullable scalars as `T | null | undefined`.
type Nullable = number | null | undefined;

type RetentionPoint = {
  readonly month: string;
  readonly nrr: Nullable;
  readonly grr: Nullable;
  readonly logoChurn: Nullable;
  readonly logoChurnCancellations: Nullable;
};
type RetentionLine = {
  readonly band: string;
  readonly points: ReadonlyArray<RetentionPoint>;
};
type DimensionSeries = {
  readonly ttm: ReadonlyArray<RetentionLine>;
  readonly mom: ReadonlyArray<RetentionLine>;
};
export type RetentionSeries = {
  readonly seat: DimensionSeries;
  readonly tenure: DimensionSeries;
};

type MetricKey = "nrr" | "grr" | "logoChurn" | "logoChurnCancellations";

// Both logo-churn measures are plotted, on the same axis so they can be read
// against each other. The cancellation rate is the investor convention; the
// cohort measure counts a customer who lapsed between invoices and came back, so
// it sits above. The gap between the lines is billing-timing noise, not leavers.
const METRICS: ReadonlyArray<{ key: MetricKey; label: string; hint: string }> =
  [
    {
      key: "nrr",
      label: "Net revenue retention (NRR)",
      hint: "Revenue from the base cohort now, over what it was then. Includes expansion.",
    },
    {
      key: "grr",
      label: "Gross revenue retention (GRR)",
      hint: "Same, with expansion capped at 100% — downgrade and churn only.",
    },
    {
      key: "logoChurnCancellations",
      label: "Logo churn (cancellations)",
      hint: "Customers from the base who terminally cancelled, over customers in the base. This is the one to quote.",
    },
    {
      key: "logoChurn",
      label: "Logo churn (cohort)",
      hint: "1 − (customers still paying / customers in the base). Counts a lapse between invoices as churn, so it sits above the cancellation rate.",
    },
  ];

const OVERALL_COLOR = "#111827";
const PALETTE = [
  "#2563eb",
  "#059669",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#db2777",
];

// The overall book gets the emphatic near-black line; buckets cycle the palette.
// Every chart of a dimension shares this band → color mapping so one legend
// covers them all, and a band keeps its colour across the four panels.
const colorForBand = (band: string, index: number) =>
  band === "Overall" ? OVERALL_COLOR : PALETTE[index % PALETTE.length];

function chartSeries(lines: ReadonlyArray<RetentionLine>, metric: MetricKey) {
  return lines.map((line, index) => ({
    name: line.band,
    type: "line" as const,
    color: colorForBand(line.band, index),
    points: line.points.map((point) => ({
      month: point.month,
      value: point[metric],
    })),
  }));
}

function Legend({ lines }: { lines: ReadonlyArray<RetentionLine> }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
      {lines.map((line, index) => (
        <span key={line.band} className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: colorForBand(line.band, index) }}
          />
          {line.band}
        </span>
      ))}
    </div>
  );
}

function DimensionCharts({
  title,
  dimension,
}: {
  title: string;
  dimension: DimensionSeries;
}) {
  const [basis, setBasis] = useState<"ttm" | "mom">("ttm");
  const lines = dimension[basis];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <div className="inline-flex overflow-hidden rounded border border-gray-200 text-xs">
          {(["ttm", "mom"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setBasis(option)}
              className={
                basis === option
                  ? "bg-gray-900 px-2.5 py-1 text-white"
                  : "px-2.5 py-1 text-gray-600 hover:bg-gray-50"
              }
            >
              {option === "ttm" ? "Trailing 12 mo" : "Month-over-month"}
            </button>
          ))}
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          No retention history yet — connect Stripe and sync to populate these
          charts.
        </div>
      ) : (
        <>
          <Legend lines={lines} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {METRICS.map((metric) => (
              <div
                key={metric.key}
                className="rounded-lg border border-gray-200 p-2"
                title={metric.hint}
              >
                <KpiChart
                  title={metric.label}
                  series={chartSeries(lines, metric.key)}
                  format="percent"
                  onMonthClick={() => undefined}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function RetentionCharts({
  series,
}: {
  series: RetentionSeries;
}) {
  return (
    <div className="space-y-6">
      <DimensionCharts title="By seat count" dimension={series.seat} />
      <DimensionCharts title="By tenure" dimension={series.tenure} />
    </div>
  );
}
